import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { api_host } from "../../utils/settings";

// 1. Define the type for the client config
interface ClientConfig {
  config_id: number | null;
  config_name: string | null;
  config_domain: string | null;
  config_logo_url: string | null;
  config_photo_spec: string | null;
  config_is_active: boolean | null;
  config_loading: boolean;
  config_error: string | null;
}

// 2. Create the context with default values
const ClientConfigContext = createContext<ClientConfig>({
  config_id: null,
  config_name: null,
  config_domain: null,
  config_logo_url: null,
  config_photo_spec: null,
  config_is_active: null,
  config_loading: true,
  config_error: null,
});

// 3. Custom hook for easy access
export const useClientConfig = () => useContext(ClientConfigContext);

// 4. Provider props type
interface ClientConfigProviderProps {
  children: ReactNode;
}

// 5. Provider component
export const ClientConfigProvider: React.FC<ClientConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<ClientConfig>({
    config_id: null,
    config_name: null,
    config_domain: null,
    config_logo_url: null,
    config_photo_spec: null,
    config_is_active: null,
    config_loading: true,
    config_error: null,
  });

  useEffect(() => {
    // Fetch client configuration from API
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${api_host}/api/config`);

        if (!response.ok) {
          throw new Error(`Failed to fetch config: ${response.status}`);
        }

        const data = await response.json();

        // Map API response to context state with config_ prefix
        setConfig({
          config_id: data.id || null,
          config_name: data.name || null,
          config_domain: data.domain || null,
          config_logo_url: data.logo_url || null,
          config_photo_spec: data.photo_spec || null,
          config_is_active: data.is_active || null,
          config_loading: false,
          config_error: null,
        });
      } catch (error) {
        console.error('Error fetching client config:', error);
        setConfig({
          config_id: null,
          config_name: null,
          config_domain: null,
          config_logo_url: null,
          config_photo_spec: null,
          config_is_active: null,
          config_loading: false,
          config_error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    };

    fetchConfig();
  }, []);

  return (
    <ClientConfigContext.Provider value={config}>
      {children}
    </ClientConfigContext.Provider>
  );
};

